import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminRsvp, RequestStatus } from "@tih/shared";
import { adminFetchRsvps, adminUpdateRsvp } from "../../../api/client";
import { useEventHub } from "../../../context/EventHubContext";

export function EventRsvpsPanel() {
  const hub = useEventHub();
  const [rows, setRows] = useState<AdminRsvp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminFetchRsvps({ eventId: hub.eventId }));
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [hub.eventId]);

  async function setStatus(id: number, status: RequestStatus) {
    await adminUpdateRsvp(id, status);
    await load();
    await hub.refreshSummary();
  }

  const totalSeats = rows.reduce((sum, r) => sum + r.seats, 0);

  return (
    <section className="admin-card">
      <h2 className="admin-card-title">RSVPs for this event</h2>
      <p className="admin-muted" style={{ marginBottom: 16 }}>
        {rows.length} reservations · {totalSeats} seats. Re-submitting with the same email
        updates that person&apos;s reservation for this event. See{" "}
        <Link to="/admin/rsvps">All RSVPs</Link> for the full inbox.
      </p>
      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <p className="admin-muted">No RSVPs for this event yet.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Seats</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.createdAt).toLocaleString()}</td>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>{r.phone ?? "—"}</td>
                  <td>{r.seats}</td>
                  <td>
                    <select
                      value={r.status}
                      onChange={(e) => void setStatus(r.id, e.target.value as RequestStatus)}
                      className={`status-select status-${r.status.toLowerCase()}`}
                    >
                      <option value="NEW">New</option>
                      <option value="IN_PROGRESS">In progress</option>
                      <option value="DONE">Done</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
